// src/views/student/slice/actions.ts
import { AxiosError } from 'axios';
import studentSlice from './slice';
import API from '../../../services/ApiService';
import { AppThunk } from '../../../redux/store';
import {
  ApiError, StudentFormData, StudentGroup, UserData,
} from '../../../services/ApiService.types';

const {
  startLoadingList, finishLoadingList, setLoadingListError, setPageData, replacePageData,
  startAddingStudent, setAddingStudentError, finishAddingStudent, addStudentToList,
  startDeletingStudent, finishDeletingStudent, setDeletingStudentError, removeStudent,
  startEditingStudent, finishEditingStudent, setEditingStudentError, replaceStudent,
  setActivationLinkSentTo, resetData, setGroups, putGroup, removeGroup,
} = studentSlice.actions;

const fetchStudentsPage = (): AppThunk => (
  async (dispatch) => {
    dispatch(startLoadingList());
    try {
      const { data: { records } } = await API.students.list();
      dispatch(setPageData({ records }));
    } catch (e) {
      const { response } = e as AxiosError<ApiError>;
      dispatch(setLoadingListError(response && response.data));
    } finally {
      dispatch(finishLoadingList());
    }
  });

const refreshStudentsPage = (): AppThunk => async (dispatch) => {
  try {
    const { data: { records } } = await API.students.list();
    dispatch(replacePageData({ records }));
  } catch (e) {
    console.error('Silent refresh error:', e);
  }
};

// Oddaje dodanego ucznia — okno „Dodaj ucznia” może go potem dopisać do grupy — albo `null`, gdy się nie udało.
// Tekst serwera okno bierze z `addingError`
const addStudent = (studentData: StudentFormData): AppThunk<Promise<UserData | null>> => async (dispatch) => {
  dispatch(startAddingStudent());
  try {
    const { data: student } = await API.students.create(studentData);
    dispatch(addStudentToList(student));

    return student;
  } catch (e) {
    const { response } = e as AxiosError<ApiError>;
    dispatch(setAddingStudentError(response?.data || {}));

    return null;
  } finally {
    dispatch(finishAddingStudent());
  }
};

const deleteStudent = (studentId: number): AppThunk => async (dispatch) => {
  dispatch(startDeletingStudent());
  try {
    await API.students.delete(studentId);
    dispatch(removeStudent(studentId));
  } catch (e) {
    const { response } = e as AxiosError<ApiError>;

    // Ucznia już nie ma, np. usunięty w innej karcie — skutek ten sam, co udanego usunięcia
    if (response?.data?.code === 'STUDENT_NOT_FOUND') {
      dispatch(removeStudent(studentId));
    } else {
      dispatch(setDeletingStudentError(response?.data || {}));
    }
  } finally {
    dispatch(finishDeletingStudent());
  }
};

// ✅ Zmiana: StudentFormData zamiast UserData jako drugi parametr
const editStudent = (studentId: number, studentData: StudentFormData): AppThunk => (
  async (dispatch) => {
    dispatch(startEditingStudent());
    try {
      const { data: { activationLinkSent, ...student } } = await API.students.edit(studentId, studentData);
      dispatch(replaceStudent(student));
      // Zmiana adresu nieaktywnego ucznia od razu wysłała mu nowy link — okno edycji powie, na jaki adres
      dispatch(setActivationLinkSentTo(activationLinkSent ? student.email : null));
    } catch (e) {
      const { response } = e as AxiosError<ApiError>;
      dispatch(setEditingStudentError(response?.data || {}));
    } finally {
      dispatch(finishEditingStudent());
    }
  });

// Bez wskaźnika ładowania: ekrany pokazują grupy, gdy przyjdą, a do tego czasu działają jak bez grup
const fetchStudentGroups = (): AppThunk => async (dispatch) => {
  try {
    const { data: { records } } = await API.studentGroups.list();
    dispatch(setGroups(records));
  } catch (e) {
    console.error('Failed to load student groups:', e);
  }
};

// Dopisuje ucznia do grupy. Zapis podaje dokładny skład grupy, więc najpierw świeże grupy z serwera — członek dopisany
// w międzyczasie (np. w drugiej karcie) nie wypadnie. Bez nazwy w zapisie, żeby nie cofnąć zmiany nazwy z drugiej karty.
// Oddaje zapisaną grupę albo `null`, gdy grupy już nie ma; inne błędy idą dalej, do okna
const addStudentToGroup = (studentId: number, groupId: number): AppThunk<Promise<StudentGroup | null>> => (
  async (dispatch) => {
    const { data: { records } } = await API.studentGroups.list();
    dispatch(setGroups(records));

    const group = records.find((item) => item.id === groupId);

    if (!group) {
      return null;
    }

    const { data: savedGroup } = await API.studentGroups.update(groupId, { studentIds: [...group.studentIds, studentId] });
    dispatch(putGroup(savedGroup));

    return savedGroup;
  });

export {
  resetData,
  fetchStudentsPage,
  refreshStudentsPage,
  fetchStudentGroups,
  addStudentToGroup,
  putGroup,
  removeGroup,
  setAddingStudentError,
  addStudent,
  setDeletingStudentError,
  deleteStudent,
  setEditingStudentError,
  editStudent,
  replaceStudent,
};
